import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { BetaAssignment, BetaFeedback } from '../types';

export function useBetaAssignments(bookId?: string, readerId?: string) {
  const [assignments, setAssignments] = useState<BetaAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignments();
  }, [bookId, readerId]);

  const fetchAssignments = async () => {
    try {
      let query = supabase
        .from('beta_reader_assignments')
        .select(`
          *,
          book_ideas!inner (
            title,
            description
          ),
          profiles!beta_reader_assignments_reader_id_fkey (
            username,
            full_name
          ),
          chapter_versions(content)
        `)
        .order('created_at', { ascending: false });

      if (bookId) {
        query = query.eq('idea_id', bookId);
      }
      if (readerId) {
        query = query.eq('reader_id', readerId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setAssignments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const createAssignment = async (data: {
    applicationId: string;
    readerId: string;
    title: string;
    description?: string;
    chapterId?: string;
    documentUrl?: string;
    dueDate?: string;
  }) => {
    try {
      const { error: createError } = await supabase
        .from('beta_reader_assignments')
        .insert({
          application_id: data.applicationId,
          reader_id: data.readerId,
          idea_id: bookId,
          chapter_id: data.chapterId,
          document_url: data.documentUrl,
          title: data.title,
          description: data.description,
          status: 'pending',
          due_date: data.dueDate
        });

      if (createError) throw createError;
      await fetchAssignments();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create assignment');
    }
  };

  const updateAssignmentStatus = async (assignmentId: string, status: 'in_progress' | 'completed') => {
    try {
      const { error: updateError } = await supabase
        .from('beta_reader_assignments')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', assignmentId);

      if (updateError) throw updateError;
      await fetchAssignments();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update assignment status');
    }
  };

  return {
    assignments,
    loading,
    error,
    createAssignment,
    updateAssignmentStatus,
    refresh: fetchAssignments
  };
}

export function useAssignmentFeedback(assignmentId: string) {
  const [feedback, setFeedback] = useState<BetaFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch feedback if we have a valid assignmentId
    if (assignmentId) {
      fetchFeedback();
    } else {
      // Reset state if no assignmentId
      setFeedback([]);
      setLoading(false);
      setError(null);
    }
  }, [assignmentId]);

  const fetchFeedback = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('beta_reader_feedback')
        .select(`
          *,
          profiles:reader_id(
            username,
            full_name
          )
        `)
        .eq('assignment_id', assignmentId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      setFeedback(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const addFeedback = async (content: string) => {
    if (!assignmentId) {
      throw new Error('No assignment selected');
    }

    try {
      const { error: createError } = await supabase
        .from('beta_reader_feedback')
        .insert({
          assignment_id: assignmentId,
          reader_id: (await supabase.auth.getUser()).data.user?.id,
          content
        });

      if (createError) throw createError;
      await fetchFeedback();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to add feedback');
    }
  };

  return {
    feedback,
    loading,
    error,
    addFeedback
  };
}